import React from 'react';
import styled from 'styled-components';
import TooltipModal from '../tooltip-modal';
import { TitleWithInfo, QuestionMark } from '../../../shared/fund/info-styles';
import propTypes from '../../common/prop-types';
import useFundStateStore from '../../common/fund-state-hook';
import { recordStatisticsEvent } from '../../../../services/statistics-service';
import Fee from './fee';
import PerformanceFeeCharged from './performance-fee-charged';

/* eslint-disable no-nested-ternary */
const Container = styled.div`
  display: flex;
  flex-direction: ${props => (props.theme.isMobileOrTablet ? 'column' : 'row')};

  & > div {
    min-width: ${props => (props.theme.isMobile ? '100%' : '33%')};
    display: flex;
    flex-direction: column;
    margin-bottom: 40px;

    > .title {
      font: ${props => props.theme.fontWeightBold} 14px/120% ${props => props.theme.fontFamilyBody};
      color: ${props => props.theme.secondaryColor};
      letter-spacing: 0.1px;
      text-transform: uppercase;
      margin-bottom: 4px;
      display: flex;
      justify-content: flex-start;
      height: 16px;

      .question-mark {
        ${QuestionMark};
      }
    }

    > .help {
      ${TitleWithInfo};
    }

    > .fee {
      > * {
        font: ${props => props.theme.fontWeightBold} 32px/100% ${props => props.theme.fontFamilyBody};
        color: ${props => props.theme.bodyColor};
        letter-spacing: 0.1px;
      }

      > .small {
        margin-left: 4px;
        font-weight: ${props => props.theme.fontWeightBold};
        font-size: 19px;
      }

      > .date {
        font-size: 14px;
        line-height: 16px;
        font-weight: ${props => props.theme.fontWeightRegular};
        display: ${props => (props.theme.isMobile ? 'inline' : 'block')};
        margin-left: ${props => (props.theme.isMobile ? 8 : 0)}px;
      }
    }

    > .performance-fee-charged {
      margin-top: 4px;
    }
  }

  & > div:last-child {
    margin-bottom: 0;
  }

  ${props => props.theme.isLarge && `
    & > div:nth-child(1) {
      min-width: 25%;
    }
    & > div:nth-child(2) {
      min-width: 25%;
    }
  `};
`;

const costRatioHelp = [
  'This is the realised total cost ratio which represents the actual fees charged over the course of the financial year, as seen in a financial statement. This includes management or investment fees, performance fees, administration fees and any other fees for underlying funds or similarly outsourced fee arrangements. The purpose of this metric is to provide a level of comparability to support informed decisions when considering the cost of an investment across different investment options.',
];

const managementFeeHelp = [
  'This is the ongoing fee for managing an investment in a fund. Charged as a percentage of the total investment amount under management.',
];

export default function Fees({ fund }) {
  const {
    id,
    managementFee,
    totalCostRatio,
    financialYearEndFeesDate,
    isPerformanceFeeChargedUnknown,
    isPerformanceFeeChargedYes,
  } = fund;

  const set = useFundStateStore(state => state.set);

  const showTooltipModal = (title, helpContent) => event => {
    event.preventDefault();
    set(state => {
      state.showTooltip = true;
      state.tooltipTitle = title;
      state.tooltipContent = helpContent;
    });

    recordStatisticsEvent({
      event: 'fund_profile_tooltip_clicked',
      statisticable: 'fund',
      id,
      clicked_url: title.replace(/[\s,/]/g, '_'),
      page: window.location.pathname,
    });
  };

  return (
    <Container>
      <Fee title="total cost ratio" value={totalCostRatio} date={financialYearEndFeesDate} helpContent={costRatioHelp} handleClick={showTooltipModal} />
      <Fee title="management fee" value={managementFee} helpContent={managementFeeHelp} handleClick={showTooltipModal} />
      {!isPerformanceFeeChargedUnknown && (
        <div>
          <div className="title">
            <span className="fee-title">Performance Fee</span>
          </div>
          <div className="performance-fee-charged">
            <PerformanceFeeCharged isPerformanceFeeCharged={isPerformanceFeeChargedYes} />
          </div>
        </div>
      )}
      <TooltipModal />
    </Container>
  );
}

/* eslint-disable-next-line react/require-default-props */
Fees.propTypes = { fund: propTypes.fund };
